import {
  CODE_EMPTY_STATE,
  DEFAULT_PAYLOAD,
  ENVIRONMENT_VARIABLES_EMPTY_STATE,
  TEST_RUN_EMPTY_STATE,
} from './defaults';
import type { CustomFunctionCompleteState } from './state';
import type { CustomFunctionModel, EnvironmentVariable } from './types';

const logger = console;

/**
 * Helper to filter out empty environment variables from the state value before passing to other components
 *
 * @param envVars - The environment variables to filter
 * @returns The filtered environment variables
 */
export function filterEmptyEnvVars(
  envVars: EnvironmentVariable[],
): EnvironmentVariable[] {
  return envVars.filter((envVar) => envVar.name.trim() !== '');
}

/**
 * Format the environment variables to be used in the test JSON under the `environment` key.
 *
 * @param envVars - The environment variables to format
 * @returns The formatted environment variables
 */
export function formatJsonEnvironmentObject(
  envVars: EnvironmentVariable[],
): Record<string, string> {
  return Object.fromEntries(
    envVars.map((envVar) => [envVar.name, `\${${envVar.name}}`]),
  );
}

/**
 * Update the test JSON environment variables when the environment variables are updated
 *
 * @param options - The current test JSON payload and environment variables
 * @returns The updated test JSON, including the new environment variables
 */
export function getNewTestStringWithEnvVars({
  testJson,
  envVars: newEnvVars,
}: CustomFunctionCompleteState): string {
  try {
    let parsedTestJson;
    try {
      parsedTestJson = JSON.parse(testJson);
    } catch (error) {
      logger.warn(`Error parsing test JSON: ${error}`);
      parsedTestJson = getDefaultTestString([]);
    }
    const { environment = {}, ...rest } = parsedTestJson;

    // Get a clean set of the new environment variables
    const fullNewEnvironmentObject = formatJsonEnvironmentObject(
      filterEmptyEnvVars(newEnvVars),
    );
    // We're only going to overwrite the changed environment variables
    const newEnvironmentObject: Record<string, string> = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const envVarName of Object.keys(fullNewEnvironmentObject)) {
      if (!(envVarName in environment)) {
        // If this is a new name, reset it to the default template string value value
        newEnvironmentObject[envVarName] = fullNewEnvironmentObject[envVarName];
      } else {
        // If this is an existing env var name, leave it as-is
        newEnvironmentObject[envVarName] = environment[envVarName];
      }
    }
    const newValue = {
      environment: newEnvironmentObject,
      ...rest,
    };
    return JSON.stringify(newValue, null, 2);
  } catch (error) {
    logger.warn(`Error parsing test JSON: ${error}`);
    // If the test JSON is invalid, then we should not update it
    return testJson;
  }
}

/**
 * Get default test string (JSON)
 *
 * @param envVars - The environment variables to use in the test JSON
 * @returns The default test string
 */
export function getDefaultTestString(envVars: EnvironmentVariable[]): string {
  const value = {
    environment: formatJsonEnvironmentObject(envVars),
    payload: DEFAULT_PAYLOAD,
  };

  return JSON.stringify(value, null, 2);
}

/**
 * Hydrate the custom function state from the custom function model, falling back to defaults
 *
 * @param customFunctionModel - The custom function model from GraphQL
 * @returns The custom function complete state
 */
export function getHydratedCustomFunctionState(
  customFunctionModel: CustomFunctionModel | undefined,
): CustomFunctionCompleteState {
  const envVars: EnvironmentVariable[] = customFunctionModel?.context
    .userDefinedEnv
    ? Object.entries(customFunctionModel.context.userDefinedEnv).map(
        ([name, value]) => ({
          name,
          value,
          isSecret: true,
        }),
      )
    : ENVIRONMENT_VARIABLES_EMPTY_STATE;

  return {
    code: customFunctionModel?.code ?? CODE_EMPTY_STATE,
    envVars,
    testJson: getDefaultTestString(envVars),
    testRun: TEST_RUN_EMPTY_STATE,
  };
}
